/*
  shop-v2.js
  Behavior for the Shop page V2 sections (sections/shop-v2-*.liquid). Every section includes this
  file, so it guards against running twice. Custom elements:
    <eko-shop-hero>   fading slider (dots, arrows, optional autoplay)
    <eko-shop-tabs>   Eko software audience tabs + arrows + photo crossfade
    <eko-shop-video>  poster/play + CORE 300 / CORE 500 toggle
  Plus <article data-shop-card> (product grid swatches -> price / image / link) and the trust
  panel rows (open the slide-in badge-drawer).
*/
(function () {
  if (window.__ekoShopV2) return;
  window.__ekoShopV2 = true;

  /* ---------------- Hero slider ---------------- */
  class EkoShopHero extends HTMLElement {
    connectedCallback() {
      // "Fit to screen" slides: how far down the page the hero starts (announcement bar, plus the header on
      // phones where it is not overlaid), so min-height can be 100svh minus that and the hero ends at the screen edge.
      const setTop = () => {
        let top = this.getBoundingClientRect().top + window.scrollY;
        // Optional (section setting): also take off the header when it floats over the hero (desktop) - on phones it is
        // already above the hero, so it is part of 'top'.
        if (this.dataset.subtractHeader === 'true') {
          const header = document.querySelector('.header-wrapper');
          if (header && getComputedStyle(header).position !== 'relative' && getComputedStyle(header).position !== 'static') top += header.offsetHeight;
        }
        this.style.setProperty('--eko-hero-top', Math.max(0, Math.round(top)) + 'px');
      };
      setTop();
      window.addEventListener('resize', setTop);
      window.addEventListener('load', setTop);
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.index = 0;
      if (this.slides.length < 2) return;

      const prev = this.querySelector('[data-hero-prev]');
      const next = this.querySelector('[data-hero-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1, true));
      if (next) next.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot) =>
        dot.addEventListener('click', () => this.go(Number(dot.dataset.heroDot), true))
      );

      // Swipe between slides on touch screens: a mostly-horizontal drag of 40px or more (left = next, right = previous).
      let startX = 0, startY = 0, tracking = false;
      this.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) { tracking = false; return; }
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
      }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(this.index + (dx < 0 ? 1 : -1), true);
      }, { passive: true });
      this.addEventListener('touchcancel', () => { tracking = false; }, { passive: true });

      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Number(this.dataset.interval) || 6000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.autoplay && !this.reducedMotion) {
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
        this.start();
      }
    }

    disconnectedCallback() { this.stop(); }

    start() {
      this.stop();
      if (!this.autoplay || this.reducedMotion) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    go(i, user) {
      const count = this.slides.length;
      this.index = ((i % count) + count) % count;
      this.slides.forEach((slide, n) => {
        const active = n === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      this.dots.forEach((dot, n) => dot.classList.toggle('is-active', n === this.index));
      // Arrows/dots follow the active slide's "Arrows and dots" setting (white / black).
      const controls = this.querySelector('[data-hero-controls]');
      const active = this.slides[this.index];
      if (controls && active) controls.dataset.controlsStyle = active.dataset.controlsStyle || 'light';
      if (user && this.autoplay) this.start();
    }
  }

  /* ---------------- Eko software tabs ---------------- */
  class EkoShopTabs extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-tab-btn]'));
      this.panes = Array.from(this.querySelectorAll('[data-tab-pane]'));
      this.photos = Array.from(this.querySelectorAll('[data-tab-photo]'));
      this.index = 0;

      this.buttons.forEach((btn) =>
        btn.addEventListener('click', () => this.go(Number(btn.dataset.tabBtn)))
      );
      const prev = this.querySelector('[data-tab-prev]');
      const next = this.querySelector('[data-tab-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
    }

    go(i) {
      const count = this.buttons.length;
      if (!count) return;
      this.index = ((i % count) + count) % count;
      const set = (list, attr) =>
        list.forEach((el, n) => el.classList.toggle('is-active', n === this.index));
      set(this.buttons);
      set(this.panes);
      set(this.photos);
      this.buttons.forEach((btn, n) =>
        btn.setAttribute('aria-selected', n === this.index ? 'true' : 'false')
      );
    }
  }

  /* ---------------- Video toggle ---------------- */
  class EkoShopVideo extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-video-item]'));
      this.tabs = Array.from(this.querySelectorAll('[data-video-tab]'));
      this.tabs.forEach((tab) =>
        tab.addEventListener('click', () => this.show(Number(tab.dataset.videoTab)))
      );
      this.items.forEach((item) => {
        const play = item.querySelector('[data-video-play]');
        if (play) play.addEventListener('click', () => this.play(item, play));
      });
    }

    show(i) {
      this.items.forEach((item, n) => {
        item.classList.toggle('is-active', n === i);
        if (n !== i) this.reset(item);
      });
      this.tabs.forEach((tab, n) => {
        tab.classList.toggle('is-active', n === i);
        tab.setAttribute('aria-selected', n === i ? 'true' : 'false');
      });
    }

    play(item, button) {
      const src = button.dataset.videoSrc;
      const embed = button.dataset.videoEmbed;
      let el;
      if (src) {
        el = document.createElement('video');
        el.src = src;
        el.controls = true;
        el.autoplay = true;
        el.playsInline = true;
        const poster = item.querySelector('.eko-shop-video__poster');
        if (poster) el.poster = poster.currentSrc || poster.src;
      } else if (embed) {
        el = document.createElement('iframe');
        el.src = embed;
        el.allow = 'autoplay; fullscreen; picture-in-picture';
        el.allowFullscreen = true;
        el.title = button.getAttribute('aria-label') || 'Video';
      } else {
        return;
      }
      el.setAttribute('data-video-live', '');
      item.appendChild(el);
      button.hidden = true;
      if (el.focus) el.focus();
    }

    reset(item) {
      const live = item.querySelector('[data-video-live]');
      if (live) live.remove();
      const play = item.querySelector('[data-video-play]');
      if (play) play.hidden = false;
    }
  }

  /* ---------------- Product card (swatches) ---------------- */
  function initCard(card) {
    if (card.__ekoShopCard) return;
    card.__ekoShopCard = true;

    const dataEl = card.querySelector('[data-card-variants]');
    let variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const byId = {};
    variants.forEach((v) => { byId[v.id] = v; });

    const img = card.querySelector('[data-card-img]');
    const now = card.querySelector('[data-card-now]');
    const was = card.querySelector('[data-card-was]');
    const shipping = card.querySelector('[data-card-shipping]');
    const shippingText = card.querySelector('[data-card-shipping-text]');
    const links = card.querySelectorAll('[data-card-title-link], [data-card-media-link], [data-card-cta]');
    const base = card.dataset.productUrl;
    const swatches = Array.from(card.querySelectorAll('.eko-shop-card__swatch'));
    const preloaded = {};

    function preload(v) {
      if (!v.image || preloaded[v.id]) return;
      preloaded[v.id] = true;
      const i = new Image();
      if (v.srcset) i.srcset = v.srcset;
      i.src = v.image;
    }

    function select(id) {
      const v = byId[id];
      if (!v) return;
      swatches.forEach((s) => {
        const on = Number(s.dataset.variantId) === id;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      if (now) now.textContent = v.now;
      if (was) {
        was.textContent = v.was || '';
        was.hidden = !v.was;
      }
      links.forEach((a) => { a.href = base + '?variant=' + id; });
      if (shipping && shippingText) {
        const msg = v.eta || card.dataset.instockText || '';
        shipping.hidden = !(v.preorder || msg);
        const status = document.createElement('strong');
        status.textContent = v.preorder ? 'Pre-order.' : 'In-stock.';
        shippingText.replaceChildren(status, document.createTextNode(msg ? ` ${msg}` : ''));
      }

      if (img && v.image) {
        const apply = () => {
          img.src = v.image;
          if (v.srcset) img.srcset = v.srcset;
          img.alt = v.alt || img.alt;
          if (v.w && v.h) { img.width = v.w; img.height = v.h; }
          img.classList.remove('is-swapping');
        };
        img.classList.add('is-swapping');
        const probe = new Image();
        if (v.srcset) probe.srcset = v.srcset;
        probe.onload = probe.onerror = () => setTimeout(apply, 120);
        probe.src = v.image;
      }
    }

    swatches.forEach((s) => {
      const id = Number(s.dataset.variantId);
      s.addEventListener('click', () => select(id));
      s.addEventListener('mouseenter', () => byId[id] && preload(byId[id]));
      s.addEventListener('focus', () => byId[id] && preload(byId[id]));
    });
  }

  /* ---------------- Trust rows -> badge drawer ---------------- */
  function initTrust(root) {
    root.querySelectorAll('[data-trust-target]').forEach((row) => {
      if (row.__ekoShopTrust) return;
      row.__ekoShopTrust = true;
      const open = () => {
        const drawer = document.querySelector('badge-drawer');
        if (!drawer) return;
        const title = row.querySelector('.eko-shop-trust__row-title');
        drawer.open(row.dataset.trustTarget, title ? title.textContent : '', row);
      };
      row.addEventListener('click', open);
      row.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      });
    });
  }

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }
  define('eko-shop-hero', EkoShopHero);
  define('eko-shop-tabs', EkoShopTabs);
  define('eko-shop-video', EkoShopVideo);

  function scan() {
    document.querySelectorAll('[data-shop-card]').forEach(initCard);
    initTrust(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();

  // Theme editor: re-init after a section re-renders.
  document.addEventListener('shopify:section:load', scan);
})();
